import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle, XCircle, Send, Search, Eye } from 'lucide-react';

export default function ApprovalsPage() {
  const [data, setData] = useState([]);
  const [machines, setMachines] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [actionOpen, setActionOpen] = useState(false);
  const [selectedOp, setSelectedOp] = useState(null);
  const [actionType, setActionType] = useState('');
  const [comments, setComments] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    Promise.all([api.get('/operations'), api.get('/machines'), api.get('/sites')]).then(([o, m, s]) => { setData(o.data); setMachines(m.data); setSites(s.data); }).finally(() => setLoading(false));
  }, []);
  const fetch = () => api.get('/operations').then(r => setData(r.data));

  const handleAction = async () => {
    if (!selectedOp || !actionType) return;
    try {
      await api.post(`/operations/${selectedOp.operation_id}/approve`, { action: actionType, comments });
      toast.success(`Record ${actionType}ed successfully`);
      setActionOpen(false); setComments(''); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
  };

  const openAction = (op, type) => { setSelectedOp(op); setActionType(type); setComments(''); setActionOpen(true); };
  const viewHistory = async (op) => {
    try { const res = await api.get(`/approval-history/${op.operation_id}`); setHistory(res.data); } catch { setHistory([]); }
    setHistoryOpen(true);
  };

  const machineName = (id) => { const m = machines.find(x => x.machine_id === id); return m ? `${m.brand} ${m.serial_number}` : id; };
  const siteName = (id) => sites.find(s => s.site_id === id)?.name || '';
  const filtered = data.filter(o => (filterStatus === 'all' || o.status === filterStatus) && (machineName(o.machine_id).toLowerCase().includes(search.toLowerCase())));

  return (
    <div data-testid="approvals-page">
      <div className="page-header">
        <div><h1 className="page-title">Approvals</h1><p className="text-sm text-slate-500 mt-1">Review and approve operational records</p></div>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="relative max-w-sm"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-white border-slate-200" /></div>
        <Select value={filterStatus} onValueChange={setFilterStatus}><SelectTrigger className="w-48 bg-white" data-testid="filter-approval-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Statuses</SelectItem><SelectItem value="draft">Draft</SelectItem><SelectItem value="submitted">Submitted</SelectItem><SelectItem value="under_review">Under Review</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent></Select>
      </div>

      <div className="data-table-wrapper">
        <Table>
          <TableHeader><TableRow className="bg-slate-50 border-b border-slate-200">
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Type</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Machine</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Site</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hours</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Created</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow> :
            filtered.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No records</TableCell></TableRow> :
            filtered.map(o => (
              <TableRow key={o.operation_id} className="hover:bg-slate-50/50">
                <TableCell><span className="text-xs font-medium bg-slate-100 text-slate-700 px-2 py-0.5 rounded capitalize">{o.event_type?.replace(/_/g, ' ')}</span></TableCell>
                <TableCell className="text-sm font-medium text-slate-900">{machineName(o.machine_id)}</TableCell>
                <TableCell className="text-sm text-slate-600">{siteName(o.site_id)}</TableCell>
                <TableCell className="text-sm text-slate-600">{o.operating_hours}h</TableCell>
                <TableCell><span className={`status-badge status-${o.status}`}>{o.status?.replace(/_/g, ' ')}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {o.status === 'draft' && <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => openAction(o, 'submit')} data-testid={`submit-${o.operation_id}`}><Send size={12} className="mr-1" />Submit</Button>}
                    {o.status === 'submitted' && <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => openAction(o, 'review')} data-testid={`review-${o.operation_id}`}>Review</Button>}
                    {(o.status === 'submitted' || o.status === 'under_review') && (
                      <>
                        <Button size="sm" className="text-xs h-7 bg-emerald-600 hover:bg-emerald-700" onClick={() => openAction(o, 'approve')} data-testid={`approve-${o.operation_id}`}><CheckCircle size={12} className="mr-1" />Approve</Button>
                        <Button size="sm" variant="destructive" className="text-xs h-7" onClick={() => openAction(o, 'reject')} data-testid={`reject-${o.operation_id}`}><XCircle size={12} className="mr-1" />Reject</Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => viewHistory(o)}><Eye size={12} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Action Dialog */}
      <Dialog open={actionOpen} onOpenChange={setActionOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading capitalize">{actionType} Record</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="form-field"><Label>Comments</Label><Textarea value={comments} onChange={e => setComments(e.target.value)} placeholder="Add comments..." className="bg-slate-50" rows={3} data-testid="approval-comments" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setActionOpen(false)}>Cancel</Button><Button onClick={handleAction} className={actionType === 'reject' ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-900 hover:bg-slate-800'} data-testid="confirm-action-btn">Confirm {actionType}</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Approval History</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2 max-h-64 overflow-y-auto">
            {history.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">No history</p> :
            history.map(h => (
              <div key={h.history_id} className="border border-slate-100 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className={`status-badge status-${h.new_status}`}>{h.action}</span>
                  <span className="text-xs text-slate-400">{new Date(h.timestamp).toLocaleString()}</span>
                </div>
                {h.comments && <p className="text-sm text-slate-600 mt-1">{h.comments}</p>}
                <p className="text-xs text-slate-400 mt-1">By: {h.performed_by}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
